import { css } from "@emotion/react";
import { styleUtils, theme } from "@minted/minted-components";
import React, { memo } from "react";

type StripeRegistryProgressBarProps = {
  dataCy?: string;
  fundedCents: number;
  goalAmountCents: number;
};

const formatDollars = (cents: number) =>
  new Intl.NumberFormat("en-US", {
    currency: "USD",
    maximumFractionDigits: 0,
    style: "currency",
  }).format(cents / 100);

const styles = {
  fundedBold: css`
    ${theme.typeStyles.bodyExtraSmallStyles}
    ${theme.typeStyles.bodyBoldStyles}
  `,
  fundedText: css`
    ${theme.typeStyles.bodyExtraSmallStyles}
    margin-top: ${theme.spacing.x6};
  `,
  progressFill: (pct: number) => css`
    background-color: ${theme.colors.accent700};
    border-radius: ${styleUtils.rem(4)};
    height: 100%;
    width: ${pct}%;
  `,
  progressTrack: css`
    background-color: ${theme.colors.accent400};
    border-radius: ${styleUtils.rem(4)};
    height: ${styleUtils.rem(8)};
    margin-top: ${theme.spacing.x2};
    overflow: hidden;
    width: 100%;
  `,
  wrapper: css`
    grid-column: span 2;
  `,
};

const StripeRegistryProgressBar = ({
  dataCy,
  fundedCents,
  goalAmountCents,
}: StripeRegistryProgressBarProps) => {
  const fundedPct =
    goalAmountCents > 0
      ? Math.min(100, (fundedCents / goalAmountCents) * 100)
      : 0;

  return (
    <div css={styles.wrapper}>
      <div css={styles.fundedText}>
        <span css={styles.fundedBold}>{formatDollars(fundedCents)}</span>
        {" funded of "}
        {formatDollars(goalAmountCents)} goal
      </div>
      <div css={styles.progressTrack} data-cy={dataCy}>
        <div css={styles.progressFill(fundedPct)} />
      </div>
    </div>
  );
};

const MemoizedStripeRegistryProgressBar = memo(StripeRegistryProgressBar);

export { MemoizedStripeRegistryProgressBar as StripeRegistryProgressBar };
